'use client'

import { Link } from '@remix-run/react'
import { motion } from 'framer-motion'
import * as React from 'react'
import { Button } from '#app/components/ui/button'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import { cn } from '#app/utils/misc.tsx'
import {
	GrantSearchFilter,
	type GrantSearchFilterValues,
} from '../grants+/GrantSearchFilter'

interface FilterOption {
	id: string
	name: string
}

export interface ProfileGrantSubscriptionFormCardProps {
	filterOptions?: {
		fundingAreas: FilterOption[]
		territories: FilterOption[]
		fundingTypes: FilterOption[]
	}
	onCancel?: () => void
	onCreateSubscription?: (data: GrantSearchFilterValues) => void
	isSubmitting?: boolean
	isPublicView?: boolean
	className?: string
}

export default function ProfileGrantSubscriptionFormCard({
	filterOptions = {
		fundingAreas: [],
		territories: [],
		fundingTypes: [],
	},
	onCancel,
	onCreateSubscription,
	isSubmitting = false,
	isPublicView = false,
	className,
}: ProfileGrantSubscriptionFormCardProps) {
	const [stagedFilters, setStagedFilters] =
		React.useState<GrantSearchFilterValues>({})

	const handleStagedFilterChange = (filters: GrantSearchFilterValues) => {
		setStagedFilters(filters)
	}

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (onCreateSubscription) {
			onCreateSubscription(stagedFilters)
			setStagedFilters({}) // Reset form after submission
		}
	}

	return (
		<motion.div
			initial={{
				opacity: 0,
				y: 20,
			}}
			animate={{
				opacity: 1,
				y: 0,
			}}
			transition={{
				duration: 0.5,
				delay: 0.1,
			}}
			className={cn('w-full max-w-md', className)}
		>
			<Card className="rounded-xl bg-brand-cyan-2 shadow-lg">
				<CardHeader className="p-6">
					<CardTitle className="text-foreground text-2xl font-semibold">
						{isPublicView
							? 'Explore Funding Profiles'
							: 'Create New Funding Profile'}
					</CardTitle>
				</CardHeader>
				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-6 px-6">
						<GrantSearchFilter
							fundingAreas={filterOptions.fundingAreas}
							territories={filterOptions.territories}
							fundingTypes={filterOptions.fundingTypes}
							initialValues={stagedFilters}
							onFilterChange={handleStagedFilterChange}
							disableGrantName={true}
						/>
					</CardContent>
					<CardFooter className="bg-muted/50 flex flex-col gap-5 p-6">
						{isPublicView ? (
							<Button variant={'primary'} asChild>
								<Link
									to="/login"
									className="focus-visible:ring-primary/70 bg-primary flex h-12 w-full items-center justify-center rounded-lg text-lg font-semibold text-white shadow-md transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-offset-2"
									aria-label="Login to get Started"
								>
									Login to get Started
								</Link>
							</Button>
						) : (
							<Button
								variant={'primary'}
								type="submit"
								disabled={isSubmitting}
								className="focus-visible:ring-primary/70 h-12 w-full rounded-lg text-lg font-semibold text-white shadow-md transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-offset-2"
								aria-label="Create Funding Profile"
							>
								{isSubmitting ? 'Creating...' : 'Create Funding Profile'}
							</Button>
						)}
						{onCancel && (
							<Button
								type="button"
								variant="outline"
								className="border-border bg-background text-foreground hover:bg-muted/70 focus-visible:ring-ring h-12 w-full rounded-lg border text-lg font-semibold shadow-md transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-offset-2"
								onClick={onCancel}
								aria-label="Cancel"
							>
								Cancel
							</Button>
						)}
					</CardFooter>
				</form>
			</Card>
		</motion.div>
	)
}
